
import { useState } from 'react';
import { ArrowLeft, Search } from 'lucide-react';
import petiusLogo from "@/assets/petius-logo.png";
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SearchModal from '@/components/SearchModal';
import UserMenu from './UserMenu';

interface StandardHeaderProps {
  title?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
}

const StandardHeader = ({ title, showBackButton = false, onBackClick }: StandardHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const getTitle = () => {
    if (title) return title;
    
    // Auto-generate titles based on route
    const path = location.pathname;
    if (path === '/feed') return t('navigation.feed');
    if (path === '/my-pets') return t('navigation.myPets');
    if (path === '/create') return t('navigation.create');
    if (path.startsWith('/pet/')) return t('navigation.profile');
    if (path.startsWith('/edit-pet/')) return t('common.edit');
    if (path === '/settings') return t('navigation.settings');
    if (path === '/notifications') return t('navigation.notifications');
    if (path.startsWith('/profile/')) return t('navigation.profile');
    
    return 'Petius';
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-4 min-h-14 flex items-center sticky top-0 z-40 safe-area-top android-header-safe">
        {/* Left side - Petius brand */}
        <div className="flex items-center">
          {showBackButton && (
            <Button variant="ghost" size="icon" onClick={handleBackClick} className="mr-2">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <Link to={user ? '/feed' : '/'} className="flex items-center gap-2">
            <img src={petiusLogo} alt="Petius" className="w-9 h-9 rounded-full object-cover" />
            <span className="text-lg font-bold petius-brand hidden sm:inline">Petius</span>
          </Link>
        </div>

        {/* Center - Section title */}
        <div className="flex-1 flex justify-center min-w-0 px-2">
          <h1 className="text-base font-semibold truncate">{getTitle()}</h1>
        </div>

        {/* Right side - Action icons */}
        <div className="flex items-center space-x-1">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setIsSearchModalOpen(true)}
            className="h-9 w-9"
          >
            <Search className="h-5 w-5" />
          </Button>
          
          <UserMenu />
        </div>
      </header>
      
      <SearchModal 
        isOpen={isSearchModalOpen} 
        onClose={() => setIsSearchModalOpen(false)} 
      />
    </>
  );
};

export default StandardHeader;
